import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { canAccess, canEditProjectAssigned, isFreelancer } from "../utils/permissions";
import { useFreelancerProjects } from "../hooks/useFreelancerProjects";
import { useUnsavedChanges, useGuardedNavigate } from "../contexts/UnsavedChangesContext.jsx";
import {
  getAllEstimates,
  trackEditor,
  updateEstimateObjektgliederung,
} from "../services/azureCostEstimatesService";
import { getAllProjectsWithTags } from "../services/azureProjectsService";
import { getProjectAssignments } from "../services/azureProjectAssignmentService";
import { EstimateTableProvider } from "../services/costs/providers/estimateTableProvider";
import { useEstimateEditor } from "../services/costs/useEstimateEditor";
import EstimateEditor from "../components/costs/EstimateEditor";
import CostControlEditor from "../components/costs/CostControlEditor";
import EstimateSidebar from "../components/costs/EstimateSidebar";
import PendenzMembersModal from "../components/PendenzMembersModal";

const CostEstimateEditorWrapper = () => {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { registerGuard } = useUnsavedChanges();
  const guardedNavigate = useGuardedNavigate();
  const isFreelancerUser = isFreelancer(user);
  const { canAccessProject } = useFreelancerProjects(user);

  const [estimate, setEstimate] = useState(null);
  const [linkedProject, setLinkedProject] = useState(null);
  const [projectEstimates, setProjectEstimates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingEstimate, setLoadingEstimate] = useState(true);
  const [provider] = useState(() => new EstimateTableProvider());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMembers, setShowMembers] = useState(false);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const next = !prev;
      const contentArea = document.querySelector(".content-area");
      if (contentArea) {
        contentArea.classList.toggle("fullscreen", next);
      }
      return next;
    });
  }, []);

  // Clean up fullscreen class on unmount
  useEffect(() => {
    return () => {
      const contentArea = document.querySelector(".content-area");
      if (contentArea) contentArea.classList.remove("fullscreen");
    };
  }, []);

  const editor = useEstimateEditor(provider);

  // Verlassen des Editors (Hauptnavigation, Subtabs, Zurueck-Button) fragt bei
  // ungespeicherten Aenderungen nach — Fenster schliessen deckt der Browser via
  // beforeunload ab (useEstimateEditor).
  useEffect(
    () =>
      registerGuard({
        label: estimate?.name,
        isDirty: () => editor.isDirty,
        save: () => editor.saveBatch("manual"),
      }),
    [registerGuard, editor.isDirty, editor.saveBatch, estimate]
  );

  // Schreibrecht ist projektbezogen und mit Pendenzen/Termine/Controlling synchron:
  // Admin/PL immer, sonst nur wer dem verknüpften Projekt zugeordnet ist (gemeinsame
  // Tabelle projectEmployeeAssignments, ADR 0020). Ohne verknüpftes Projekt bleibt es
  // bei Admin/PL, da dann keine Zuordnungen existieren.
  const readOnly = !canEditProjectAssigned(user, assignments);

  // Zuordnungen des verknüpften Projekts neu laden (nach Änderung im Zuordnen-Modal).
  const reloadAssignments = useCallback(async (code) => {
    const projectCode = code || linkedProject?.code || estimate?.linkedProjectCode;
    if (!projectCode) {
      setAssignments([]);
      return;
    }
    try {
      setAssignments(await getProjectAssignments(projectCode));
    } catch {
      /* nicht kritisch – readOnly bleibt bestehen */
    }
  }, [linkedProject, estimate]);

  useEffect(() => {
    loadEstimate();
  }, [estimateId]);

  useEffect(() => {
    if (estimate && provider) {
      // Read phase from URL query param, fallback to dossier's currentPhase
      const urlPhase = searchParams.get("phase");
      let initialPhase = null;

      if (urlPhase) {
        // Find matching phase by prefix (e.g., "31" matches "31 - Vorprojekt")
        const dossierPhases = estimate.phases?.length > 0
          ? estimate.phases
          : ["21 - Machbarkeit", "31 - Vorprojekt", "32 - Bauprojekt", "33 - Bewilligung", "41 - Ausschreibung"];
        const match = dossierPhases.find((p) => p.startsWith(urlPhase));
        if (match) initialPhase = match;
      }

      editor.init(estimateId, estimate, initialPhase);

      // Track this user as an editor
      const userName = user?.userDetails || user?.email || user?.name || "";
      if (userName && estimateId) {
        trackEditor(estimateId, userName);
      }
    }
  }, [estimate, provider]);

  // Sync phase to URL when it changes
  const handleSwitchPhase = useCallback((phaseName) => {
    editor.switchPhase(phaseName);
    // Extract phase prefix for URL (e.g., "31 - Vorprojekt" → "31")
    const prefix = phaseName.split(" ")[0];
    setSearchParams({ phase: prefix }, { replace: true });
  }, [editor.switchPhase, setSearchParams]);

  // Phase umbenennen (US-KOST-019) — URL-Prefix nachziehen, wenn die aktuell
  // geöffnete Phase umbenannt wurde. Fehler wirft der Hook; das Modal zeigt sie.
  const handleRenamePhase = useCallback(async (oldName, newName) => {
    const wasCurrent = editor.currentPhase === oldName;
    const renamed = await editor.renamePhase(oldName, newName);
    if (renamed && wasCurrent) {
      setSearchParams({ phase: newName.trim().split(" ")[0] }, { replace: true });
    }
    return renamed;
  }, [editor.renamePhase, editor.currentPhase, setSearchParams]);

  // Phase löschen (US-KOST-019) — der Hook wechselt bei Bedarf auf die
  // Vorgänger-Phase und gibt deren Namen zurück; URL-Prefix nachziehen.
  const handleDeletePhase = useCallback(async (phaseName) => {
    const fallback = await editor.deletePhase(phaseName);
    if (fallback) {
      setSearchParams({ phase: fallback.trim().split(" ")[0] }, { replace: true });
    }
    return fallback;
  }, [editor.deletePhase, setSearchParams]);

  // Objektgliederung (US-KOST-023): seit 2026-09-30 an der aktuellen
  // Kostenkontroll-Phase (Meta-Zeile `__ctrlmeta__<phase>`, ADR 0064 Nachtrag).
  // Ist an der Phase noch nichts gesetzt (null), gilt der frühere Dossier-Wert
  // (`costEstimates.Objektgliederung`) als Vorgabe, bis die Phase einen
  // eigenen Wert bekommt. Ein bewusst geleertes Feld ("") bleibt leer.
  const handleSaveObjektgliederung = useCallback(
    (value) => editor.saveObjektgliederung(value),
    [editor.saveObjektgliederung]
  );
  const phaseObjektgliederung =
    editor.objektgliederung ?? estimate?.objektgliederung ?? "";

  // Bis 2026-09-23 wurde die Objektgliederung je Browser gemerkt
  // (localStorage `costControlZa:<estimateId>`, US-KOST-022). Einmalige
  // Uebernahme ins Dossier, sofern dort noch nichts steht.
  const readLegacyObjektgliederung = (id) => {
    try {
      const raw = localStorage.getItem(`costControlZa:${id}`);
      const v = raw ? JSON.parse(raw)?.objektgliederung : "";
      return typeof v === "string" ? v.trim() : "";
    } catch {
      return "";
    }
  };

  const loadEstimate = async () => {
    try {
      setLoadingEstimate(true);
      const estimates = await getAllEstimates();
      let found = estimates.find((e) => e.id === estimateId);

      if (!found) {
        navigate("/kosten");
        return;
      }

      // Freelancer access check
      if (isFreelancerUser && found.linkedProjectCode) {
        if (!canAccessProject(found.linkedProjectCode)) {
          navigate("/kosten");
          return;
        }
      }

      if (!found.objektgliederung) {
        const legacy = readLegacyObjektgliederung(estimateId);
        if (legacy) {
          try {
            await updateEstimateObjektgliederung(estimateId, legacy);
            found = { ...found, objektgliederung: legacy };
          } catch {
            /* nicht kritisch — Sidebar zeigt dann ein leeres Feld */
          }
        }
      }

      setEstimate(found);

      // Dossiers desselben Projekts (fuer KV-Basis-Quellwahl, KOST-006).
      if (found.linkedProjectCode) {
        setProjectEstimates(
          estimates.filter((e) => e.linkedProjectCode === found.linkedProjectCode)
        );
      } else {
        setProjectEstimates([found]);
      }

      // Projekt-Zuordnungen laden (Schreibrecht, synchron mit Pendenzen etc.).
      if (found.linkedProjectCode) {
        try {
          setAssignments(await getProjectAssignments(found.linkedProjectCode));
        } catch {
          setAssignments([]);
        }
      } else {
        setAssignments([]);
      }

      // Load linked project name
      if (found.linkedProjectCode) {
        try {
          const projects = await getAllProjectsWithTags("project");
          const proj = projects.find((p) => p.code === found.linkedProjectCode);
          // auftraggeberin/adresse: Fallbacks fuer die Zahlungsanweisung (US-KOST-022)
          if (proj) {
            setLinkedProject({
              code: proj.code,
              name: proj.title,
              auftraggeberin: proj.auftraggeberin || "",
              adresse: proj.adresse || "",
            });
          }
        } catch (e) {
          // non-critical
        }
      }
    } catch (error) {
      console.error("Error loading estimate:", error);
      navigate("/kosten");
    } finally {
      setLoadingEstimate(false);
    }
  };

  if (authLoading || loadingEstimate) {
    return <div className="loading">Dossier wird geladen...</div>;
  }

  if (!user || !canAccess(user, "kostenschaetzung")) {
    return (
      <div className="access-denied">
        <h3>Zugriff verweigert</h3>
        <p>Sie haben keine Berechtigung für diesen Bereich.</p>
      </div>
    );
  }

  if (!estimate) return null;

  return (
    <div className="estimate-editor-layout">
      <EstimateSidebar
        onBack={() => guardedNavigate("/kosten")}
        estimateName={estimate.name}
        linkedProject={linkedProject}
        currentPhase={editor.currentPhase}
        phases={editor.phases}
        onSwitchPhase={handleSwitchPhase}
        onAddPhase={editor.addPhase}
        onRenamePhase={handleRenamePhase}
        onDeletePhase={handleDeletePhase}
        estimateId={estimateId}
        levelFilter={editor.levelFilter}
        setLevelFilter={editor.setLevelFilter}
        showInactive={editor.showInactive}
        setShowInactive={editor.setShowInactive}
        readOnly={readOnly}
        variants={editor.variants}
        onCreateVariant={editor.createVariant}
        onToggleVariant={editor.toggleVariant}
        onDeleteVariant={editor.removeVariant}
        onApplyVariant={editor.applyVariant}
        getVariantConflicts={editor.getVariantConflicts}
        searchTerm={editor.searchTerm}
        setSearchTerm={editor.setSearchTerm}
        activeFilters={editor.activeFilters}
        addFilter={editor.addFilter}
        removeFilter={editor.removeFilter}
        filterSums={editor.filterSums}
        setFilterSums={editor.setFilterSums}
        objektgliederung={phaseObjektgliederung}
        onSaveObjektgliederung={readOnly ? null : handleSaveObjektgliederung}
      />
      <div className="estimate-editor-main">
        {editor.isLoading ? (
          <div className="loading">BKP-Daten werden geladen...</div>
        ) : editor.error ? (
          <div className="error-message">Fehler: {editor.error}</div>
        ) : (editor.currentPhase || "").trim().startsWith("41") ? (
          // Ab SIA-Phase 41 (Ausschreibung) wird aus der Kostenschaetzung eine
          // Kostenkontrolle (KOST-007): eigener Editor statt Ausmass-Tabelle.
          <CostControlEditor
            estimateId={estimateId}
            tree={editor.tree}
            expanded={editor.expanded}
            toggleExpand={editor.toggleExpand}
            showInactive={editor.showInactive}
            setShowInactive={editor.setShowInactive}
            editControlField={editor.editControlField}
            activatePosition={editor.activatePosition}
            deletePosition={editor.deleteControlPosition}
            addChild={editor.addChild}
            saveBatch={editor.saveBatch}
            isSaving={editor.isSaving}
            isDirty={editor.isDirty}
            lastSavedAt={editor.lastSavedAt}
            lastSaveKind={editor.lastSaveKind}
            readOnly={readOnly}
            currentPhase={editor.currentPhase}
            estimateName={estimate.name}
            linkedProject={linkedProject}
            importKvBase={editor.importKvBase}
            kvBaseImported={editor.kvBaseImported}
            projectEstimates={projectEstimates}
            currentEstimateId={estimateId}
            estimateClient={estimate.client || ""}
            currentUserName={user?.teamMember?.name || user?.name || ""}
            objektgliederung={phaseObjektgliederung}
            searchTerm={editor.searchTerm}
            activeFilters={editor.activeFilters}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            canManageMembers={!readOnly && !!linkedProject}
            onManageMembers={() => setShowMembers(true)}
          />
        ) : (
          <EstimateEditor
            tree={editor.tree}
            expanded={editor.expanded}
            levelFilter={editor.levelFilter}
            searchTerm={editor.searchTerm}
            activeFilters={editor.activeFilters}
            filterSums={editor.filterSums}
            showInactive={editor.showInactive}
            isSaving={editor.isSaving}
            lastSavedAt={editor.lastSavedAt}
            lastSaveKind={editor.lastSaveKind}
            isDirty={editor.isDirty}
            editCell={editor.editCell}
            toggleActive={editor.toggleActive}
            deactivateParentForChild={editor.deactivateParentForChild}
            addChild4Digit={editor.addChild4Digit}
            deleteNode={editor.deleteNode}
            toggleExpand={editor.toggleExpand}
            saveBatch={editor.saveBatch}
            undo={editor.undo}
            undoStack={editor.undoStack}
            beginUndoBatch={editor.beginUndoBatch}
            endUndoBatch={editor.endUndoBatch}
            readOnly={readOnly}
            variants={editor.variants}
            getEffectiveValue={editor.getEffectiveValue}
            getVariantConflicts={editor.getVariantConflicts}
            applyVariantOverrides={editor.applyVariantOverrides}
            addPositionToVariant={editor.addPositionToVariant}
            removePositionFromVariant={editor.removePositionFromVariant}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            customColumns={editor.customColumns}
            addCustomColumn={editor.addCustomColumn}
            deleteCustomColumn={editor.deleteCustomColumn}
            renameCustomColumn={editor.renameCustomColumn}
            editCustomField={editor.editCustomField}
            estimateName={estimate.name}
            linkedProject={linkedProject}
            currentPhase={editor.currentPhase}
            canManageMembers={!readOnly && !!linkedProject}
            onManageMembers={() => setShowMembers(true)}
          />
        )}
      </div>

      {/* Personen dem Projekt zuordnen – gemeinsame Zuordnung (projectEmployeeAssignments)
          wie Pendenzen/Termine/Controlling; wer zugeordnet ist, darf die Kostenschätzung
          bearbeiten (ADR 0020). Modal hier zentral, aus beiden Editor-Toolbars geöffnet. */}
      {showMembers && linkedProject && (
        <PendenzMembersModal
          projectCode={linkedProject.code}
          projectTitle={linkedProject.name}
          onClose={() => setShowMembers(false)}
          onChanged={() => {
            reloadAssignments();
            setShowMembers(false);
          }}
        />
      )}
    </div>
  );
};

export default CostEstimateEditorWrapper;
